/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { noop } from 'lodash';

import { TickFilledIcon } from '@motorway/motorway-storybook-cra';

import stylesExpandable from 'Components/shared/Expandable/Expandable.module.scss';
import ExpandableIcon from 'Components/shared/Expandable/ExpandableIcon/ExpandableIcon';
import SectionButton from 'Components/shared/SectionButton/SectionButton';
import { isFunction, isUndefined } from 'Utilities/helpers';
import useExpandable from 'Utilities/hooks/useExpandable';
import proPropTypes from 'Utilities/proPropTypes';

import styles from './ExpandableListItem.module.scss';

const ExpandableListItem = ({
	badge: Badge,
	children,
	classNames,
	headerAction,
	id,
	isAnimated,
	isDirty,
	isExpanded,
	isSmall,
	onClick,
	shouldShowBadge = false,
	title,
}) => {
	const [showTransition, setShowTransition] = useState(false);
	const noTransition = isUndefined(isExpanded) && isDirty;
	const { buttonProps, contentRef } = useExpandable({ id, isExpanded, onClick });

	const headerProps = { ...buttonProps, tabIndex: 0 };

	useEffect(() => {
		const timeoutId = setTimeout(() => setShowTransition(true));

		if (isExpanded && isDirty) {
			onClick(true);
		}

		return () => clearTimeout(timeoutId);
	}, []);

	return (
		<div
			className={cx(stylesExpandable.component, styles.container, classNames.container, {
				[stylesExpandable.noTransition]: !showTransition,
				[stylesExpandable.expanded]: isExpanded,
				[styles.isSmall]: isSmall,
				[classNames.expanded]: isExpanded,
			})}
		>
			<SectionButton
				{...headerProps}
				className={cx(styles.header, classNames.header, { [classNames.animateHeader]: isAnimated })}
			>
				<h3 className={cx(styles.title, classNames.title)}>
					{title}
					{shouldShowBadge && <Badge />}
				</h3>
				{isDirty && !isExpanded && (
					<span className={styles.appliedIcon}>
						<TickFilledIcon />
					</span>
				)}
				{!!headerAction && isExpanded && <div className={styles.headerAction}>{headerAction}</div>}
				<ExpandableIcon className={styles.expandIcon} expanded={isExpanded} noTransition={noTransition} />
			</SectionButton>
			<div
				aria-hidden={!isExpanded}
				className={cx(stylesExpandable.contentWrapper, classNames.contentWrapper, styles.contentWrapper, {
					[classNames.animateContent]: isAnimated,
				})}
				id={id}
			>
				<div
					ref={contentRef}
					className={cx('expandableContent', styles.expandableContent, classNames.content)}
					style={{ height: isExpanded ? contentRef.current?.scrollHeight : '0' }}
				>
					{isFunction(children) ? children(isExpanded) : children}
				</div>
			</div>
		</div>
	);
};

ExpandableListItem.defaultProps = {
	classNames: {},
	headerAction: null,
	isAnimated: true,
	isDirty: false,
	isExpanded: false,
	isSmall: false,
	onClick: noop,
	shouldShowBadge: false,
};

ExpandableListItem.propTypes = {
	badge: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
	classNames: PropTypes.shape({
		animateContent: proPropTypes.className,
		animateHeader: proPropTypes.className,
		container: proPropTypes.className,
		content: proPropTypes.className,
		contentWrapper: proPropTypes.className,
		expanded: proPropTypes.className,
		header: proPropTypes.className,
		title: proPropTypes.className,
	}),
	headerAction: PropTypes.node,
	id: PropTypes.string.isRequired,
	isAnimated: PropTypes.bool,
	isDirty: PropTypes.bool,
	isExpanded: PropTypes.bool,
	isSmall: PropTypes.bool,
	onClick: PropTypes.func,
	shouldShowBadge: PropTypes.bool,
	title: PropTypes.node.isRequired,
};

export default ExpandableListItem;
